import DOMPurify from 'dompurify';
import { marked } from 'marked';
import TurndownService from 'turndown';
import * as turndownPluginGfm from 'turndown-plugin-gfm';

const { gfm, highlightedCodeBlock } = turndownPluginGfm;

const MARKDOWN_ALLOWED_TAGS = [
  'a',
  'blockquote',
  'br',
  'code',
  'del',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
];

const MARKDOWN_ALLOWED_ATTR = [
  'align',
  'alt',
  'class',
  'href',
  'rel',
  'src',
  'target',
  'title',
];

const sanitizeGeneratedHtml = (html) => DOMPurify.sanitize(html, {
  ALLOWED_TAGS: MARKDOWN_ALLOWED_TAGS,
  ALLOWED_ATTR: MARKDOWN_ALLOWED_ATTR,
  ALLOW_DATA_ATTR: false,
});

const createTurndownService = () => {
  const service = new TurndownService({
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
    emDelimiter: '*',
    headingStyle: 'atx',
  });

  service.use(gfm);
  service.use(highlightedCodeBlock);

  service.addRule('quillSyntaxBlock', {
    filter: (node) => node.nodeName === 'PRE' && node.classList.contains('ql-syntax'),
    replacement: (_content, node) => {
      const text = String(node.textContent || '').replace(/\n+$/, '');
      return text ? `\n\n\`\`\`\n${text}\n\`\`\`\n\n` : '\n\n';
    },
  });

  service.addRule('underline', {
    filter: ['u'],
    replacement: (content) => `<u>${content}</u>`,
  });

  service.addRule('flattenSpan', {
    filter: 'span',
    replacement: (content) => content,
  });

  return service;
};

const turndownService = createTurndownService();

export const markdownToHtml = (markdown) => {
  const source = String(markdown || '').trim();

  if (!source) {
    return '';
  }

  const renderedHtml = marked.parse(source, {
    breaks: true,
    gfm: true,
  });

  return sanitizeGeneratedHtml(String(renderedHtml || '')).trim();
};

export const htmlToMarkdown = (html) => {
  const source = String(html || '').trim();

  if (!source) {
    return '';
  }

  if (typeof DOMParser === 'undefined') {
    return source;
  }

  const document = new DOMParser().parseFromString(source, 'text/html');
  const normalizedHtml = document.body?.innerHTML || source;
  const markdown = turndownService.turndown(normalizedHtml);

  return markdown
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
